import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMXN } from "@/lib/format";
import { Gift, Check, Copy, Home } from "lucide-react";

export default function GiftCardSuccessPage() {
  const [status, setStatus] = useState("checking");
  const [card, setCard] = useState(null);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    poll(sessionId, 0);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line
  }, []);

  const poll = async (sid, attempt) => {
    if (attempt > 8) {
      setStatus("timeout");
      return;
    }
    try {
      const { data } = await api.get(`/gift-cards/status/${sid}`);
      if (data.payment_status === "paid") {
        setStatus("paid");
        setCard(data.gift_card);
        return;
      }
    } catch {}
    timerRef.current = setTimeout(() => poll(sid, attempt + 1), 2000);
  };

  const copyCode = () => {
    if (card?.code) {
      navigator.clipboard.writeText(card.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (status === "checking") {
    return (
      <div className="min-h-screen bg-arre-black flex items-center justify-center p-6" data-testid="gc-checking">
        <div className="text-center">
          <div className="w-20 h-20 border-2 border-arre-border border-t-arre-red rounded-full animate-spin mx-auto mb-6" />
          <h1 className="font-display text-4xl text-white tracking-wider uppercase">Activando tu tarjeta...</h1>
        </div>
      </div>
    );
  }

  if (status === "timeout" || !card) {
    return (
      <div className="min-h-screen bg-arre-black flex items-center justify-center p-6">
        <div className="text-center">
          <h1 className="font-display text-4xl text-arre-red tracking-wider uppercase mb-3">Procesando...</h1>
          <p className="text-white/70">Tu tarjeta estará activa en unos minutos. Revisa tu correo.</p>
          <Link to="/" className="mt-6 inline-block btn-arre bg-arre-red text-white font-display text-lg tracking-widest uppercase px-6 py-3">Al inicio</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-arre-black py-16 px-6" data-testid="gc-success">
      <div className="max-w-xl mx-auto text-center">
        <div className="w-20 h-20 mx-auto bg-arre-red/20 border border-arre-red flex items-center justify-center mb-6">
          <Gift className="w-10 h-10 text-arre-red" strokeWidth={1.5} />
        </div>
        <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">Tarjeta activada</div>
        <h1 className="font-display text-5xl md:text-6xl tracking-wider uppercase text-white leading-[0.9]">
          ¡Ahí va,<br /><span className="text-arre-red">compare!</span>
        </h1>
        <p className="font-serif-display italic text-lg text-white/70 mt-4">
          Tu tarjeta de regalo está lista. Guarda el código abajo y compártelo con tu compare.
        </p>

        <div className="mt-10 bg-arre-surface border-2 border-arre-red p-8 grain">
          <div className="text-[10px] tracking-[0.35em] uppercase text-arre-red mb-2">Código único</div>
          <div className="font-display text-4xl md:text-5xl tracking-[0.15em] text-white break-all" data-testid="gc-code">{card.code}</div>
          <button
            onClick={copyCode}
            className="mt-4 inline-flex items-center gap-2 text-white/70 hover:text-white text-sm tracking-widest uppercase"
            data-testid="gc-copy-btn"
          >
            <Copy className="w-4 h-4" /> {copied ? "¡Copiado!" : "Copiar código"}
          </button>
          <div className="mt-6 pt-6 border-t border-arre-border">
            <div className="text-xs tracking-widest uppercase text-white/50">Saldo</div>
            <div className="font-display text-3xl text-white mt-1">{fmtMXN(card.balance || card.initial_amount)}</div>
          </div>
        </div>

        <p className="text-sm text-white/50 mt-6">
          Para usarla, el destinatario solo ingresa este código en el checkout. Sin expiración.
        </p>
        <Link to="/" className="mt-8 inline-flex items-center gap-2 btn-arre bg-arre-red hover:bg-arre-red-dark text-white font-display text-lg tracking-widest uppercase px-6 py-3">
          <Home className="w-5 h-5" /> Volver al inicio
        </Link>
      </div>
    </div>
  );
}
